/**
 * PATH: packages/drafting/src/hooks/useDrafting.ts
 * FILE_ID: DRAFTING-HOOK-USEDRAFTING-001
 * PURPOSE: The Drafting Studio state machine — owns the Drawing IR plus
 *   tool/selection/active-layer, exposes pure-ish mutators, and keeps an
 *   undo/redo history. Both mouse edits and (later) AI /refine results
 *   funnel through the SAME mutators so the IR stays the single source.
 */
import { useCallback, useMemo, useRef, useState } from 'react'
import {
  type DrawingIR,
  type Entity,
  type EntityInput,
  type Layer,
  type ValidationReport,
} from '../ir/schema'
import { createEmptyDrawing, makeEntityId } from '../ir/defaults'
import { runValidators } from '../ir/validate'

export type Tool =
  | 'select'
  | 'line'
  | 'rect'
  | 'circle'
  | 'text'
  | 'dimension'
  | 'leader'

export interface UseDrafting {
  ir: DrawingIR
  tool: Tool
  activeLayerId: string
  selectedId: string | null
  report: ValidationReport
  canUndo: boolean
  canRedo: boolean
  setTool: (t: Tool) => void
  setActiveLayerId: (id: string) => void
  setSelectedId: (id: string | null) => void
  replaceIr: (next: DrawingIR) => void
  addEntity: (partial: EntityInput) => string
  updateEntity: (next: Entity) => void
  deleteEntity: (id: string) => void
  updateMeta: (patch: Partial<DrawingIR['meta']>) => void
  addLayer: (layer: Layer) => void
  updateLayer: (id: string, patch: Partial<Layer>) => void
  toggleLayerVisible: (id: string) => void
  undo: () => void
  redo: () => void
}

export function useDrafting(initial?: DrawingIR): UseDrafting {
  const [ir, setIr] = useState<DrawingIR>(() => initial ?? createEmptyDrawing())
  const [tool, setTool] = useState<Tool>('select')
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [activeLayerId, setActiveLayerId] = useState<string>(
    () => (initial ?? createEmptyDrawing()).layers[0]?.id ?? 'annotation',
  )

  const past = useRef<DrawingIR[]>([])
  const future = useRef<DrawingIR[]>([])
  const [, force] = useState(0)
  const rerender = useCallback(() => force((n) => n + 1), [])

  // commit — push current IR onto the undo stack, then apply `next`.
  const commit = useCallback(
    (next: DrawingIR) => {
      setIr((prev) => {
        past.current.push(prev)
        if (past.current.length > 100) past.current.shift()
        future.current = []
        return next
      })
    },
    [],
  )

  const replaceIr = useCallback((next: DrawingIR) => commit(next), [commit])

  const addEntity = useCallback(
    (partial: EntityInput) => {
      const id = partial.id ?? makeEntityId()
      const entity = { ...partial, id, layer: partial.layer ?? activeLayerId } as Entity
      commit({ ...ir, entities: [...ir.entities, entity] })
      return id
    },
    [commit, ir, activeLayerId],
  )

  const updateEntity = useCallback(
    (next: Entity) => {
      commit({ ...ir, entities: ir.entities.map((e) => (e.id === next.id ? next : e)) })
    },
    [commit, ir],
  )

  const deleteEntity = useCallback(
    (id: string) => {
      commit({ ...ir, entities: ir.entities.filter((e) => e.id !== id) })
      setSelectedId((s) => (s === id ? null : s))
    },
    [commit, ir],
  )

  const updateMeta = useCallback(
    (patch: Partial<DrawingIR['meta']>) => commit({ ...ir, meta: { ...ir.meta, ...patch } }),
    [commit, ir],
  )

  const addLayer = useCallback(
    (layer: Layer) => commit({ ...ir, layers: [...ir.layers, layer] }),
    [commit, ir],
  )

  const updateLayer = useCallback(
    (id: string, patch: Partial<Layer>) =>
      commit({ ...ir, layers: ir.layers.map((l) => (l.id === id ? { ...l, ...patch } : l)) }),
    [commit, ir],
  )

  const toggleLayerVisible = useCallback(
    (id: string) =>
      commit({
        ...ir,
        layers: ir.layers.map((l) => (l.id === id ? { ...l, visible: l.visible === false } : l)),
      }),
    [commit, ir],
  )

  const undo = useCallback(() => {
    setIr((prev) => {
      const last = past.current.pop()
      if (!last) return prev
      future.current.push(prev)
      return last
    })
    rerender()
  }, [rerender])

  const redo = useCallback(() => {
    setIr((prev) => {
      const next = future.current.pop()
      if (!next) return prev
      past.current.push(prev)
      return next
    })
    rerender()
  }, [rerender])

  const report = useMemo(() => runValidators(ir), [ir])

  return {
    ir,
    tool,
    activeLayerId,
    selectedId,
    report,
    canUndo: past.current.length > 0,
    canRedo: future.current.length > 0,
    setTool,
    setActiveLayerId,
    setSelectedId,
    replaceIr,
    addEntity,
    updateEntity,
    deleteEntity,
    updateMeta,
    addLayer,
    updateLayer,
    toggleLayerVisible,
    undo,
    redo,
  }
}
